// Source context assembly (ARCHITECTURE.md §11.3 step 2, §11.5).
//
// Turns a GapCandidate into a SourceContext: the minimum bundle of structure
// (symbols + existing edges, from the store / Layer 2) and source snippets
// (from the file system / Layer 1) the LLM needs to judge whether the two files
// are related. The gap's reason drives a human-readable GapDescription and a
// heuristic confidence.

import type { SymbolInfo } from "../analyzer/types.js";
import type { GraphEdge } from "../types.js";
import { extractSnippets } from "./snippet-extractor.js";
import {
  GAP_REASON_CONFIDENCE,
  type FileContext,
  type GapCandidate,
  type GapDescription,
  type SourceContext,
} from "./types.js";

export interface AssembleContextOptions {
  /** filePath → raw source content (Layer 1, read from the file system). */
  fileContents: Map<string, string>;
  /** filePath → symbols (Layer 2, from the store / AST graph). */
  symbols: Map<string, SymbolInfo[]>;
  /** filePath → existing edges originating from the file (Layer 2). */
  edges: Map<string, GraphEdge[]>;
}

function buildFileContext(
  filePath: string,
  options: AssembleContextOptions,
): FileContext {
  const symbols = options.symbols.get(filePath) ?? [];
  const content = options.fileContents.get(filePath) ?? "";
  return {
    filePath,
    symbols,
    existingEdges: options.edges.get(filePath) ?? [],
    snippets: extractSnippets(filePath, content, symbols),
  };
}

function describeGap(gap: GapCandidate): GapDescription {
  const description: GapDescription = {
    reason: gap.gapReason,
    confidence: GAP_REASON_CONFIDENCE[gap.gapReason],
  };
  if (gap.contractRelation) {
    const { fromArtifact, relation, toArtifact } = gap.contractRelation;
    description.contractRelation = `${fromArtifact} --${relation}--> ${toArtifact}`;
  }
  return description;
}

/**
 * Assemble the SourceContext for a single gap. `gap.fileA` becomes the primary
 * file; `gap.fileB` becomes the (single) related file. Missing source content
 * yields a FileContext with no snippets rather than failing — the LLM can still
 * reason from symbols and existing edges.
 */
export function assembleSourceContext(
  gap: GapCandidate,
  options: AssembleContextOptions,
): SourceContext {
  return {
    file: buildFileContext(gap.fileA, options),
    relatedFiles: [buildFileContext(gap.fileB, options)],
    gap: describeGap(gap),
  };
}
